import { useCallback, useEffect, useRef, useState } from "react"

type TypingPhase = "placeholder" | "typing" | "pause" | "deleting" | "prePause"

interface UseTypingAnimationOptions {
  prompts: Array<{ text: string; type: string }>
  typingSpeed?: number
  deletingSpeed?: number
  pauseDuration?: number
  prePauseDuration?: number
  speedVariance?: number
}

function randomAround(base: number, variance = 20) {
  return base + Math.floor(Math.random() * (variance * 2 + 1)) - variance
}

export function useTypingAnimation({
  prompts,
  typingSpeed = 46,
  deletingSpeed = 9,
  pauseDuration = 2400,
  prePauseDuration = 400,
  speedVariance = 20,
}: UseTypingAnimationOptions) {
  const [phase, setPhase] = useState<TypingPhase>("placeholder")
  const [promptIndex, setPromptIndex] = useState(0)
  const [charIndex, setCharIndex] = useState(0)
  const [isInView, setIsInView] = useState(false)
  const [hasEnteredView, setHasEnteredView] = useState(false)
  const typingStartedRef = useRef(false)

  const handleViewportEnter = useCallback(() => {
    setIsInView(true)
    if (!hasEnteredView) {
      setHasEnteredView(true)
      setPhase("placeholder")
    }
  }, [hasEnteredView])

  const handleViewportLeave = useCallback(() => {
    setIsInView(false)
  }, [])

  // Notify when typing starts
  useEffect(() => {
    if (phase === "typing" && !typingStartedRef.current) {
      window.dispatchEvent(new Event("landingTypingStarted"))
      typingStartedRef.current = true
    }
  }, [phase])

  // Main typing loop
  useEffect(() => {
    if (!(hasEnteredView && isInView)) return
    let timeout: ReturnType<typeof setTimeout>

    switch (phase) {
      case "placeholder":
        // Will be handled externally
        break

      case "typing": {
        const currentPrompt = prompts[promptIndex]
        if (currentPrompt && charIndex < currentPrompt.text.length) {
          timeout = setTimeout(() => setCharIndex(charIndex + 1), randomAround(typingSpeed, speedVariance))
        } else {
          timeout = setTimeout(() => setPhase("pause"), 0)
        }
        break
      }

      case "pause":
        timeout = setTimeout(() => setPhase("deleting"), randomAround(pauseDuration, 100))
        break

      case "deleting":
        if (charIndex > 0) {
          timeout = setTimeout(() => setCharIndex(charIndex - 1), randomAround(deletingSpeed, 4))
        } else {
          timeout = setTimeout(() => setPhase("prePause"), 0)
        }
        break

      case "prePause":
        timeout = setTimeout(
          () => {
            const nextIndex = promptIndex + 1
            if (nextIndex >= prompts.length) {
              setPromptIndex(0)
              setPhase("placeholder")
            } else {
              setPromptIndex(nextIndex)
              setPhase("typing")
            }
          },
          randomAround(prePauseDuration, 100),
        )
        break
    }

    return () => clearTimeout(timeout)
  }, [
    phase,
    charIndex,
    promptIndex,
    hasEnteredView,
    isInView,
    prompts,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
    prePauseDuration,
    speedVariance,
  ])

  // Pause typing when tab is hidden
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") {
        setPhase("placeholder")
        setCharIndex(0)
      }
    }
    document.addEventListener("visibilitychange", handler)
    return () => document.removeEventListener("visibilitychange", handler)
  }, [])

  const typedText =
    phase === "typing" || phase === "pause" || phase === "deleting"
      ? (prompts[promptIndex]?.text.slice(0, charIndex) ?? "")
      : ""

  const currentPromptType =
    phase === "typing" || phase === "pause" || phase === "deleting"
      ? (prompts[promptIndex]?.type ?? "neutral")
      : "neutral"

  return {
    phase,
    setPhase,
    typedText,
    currentPromptType,
    promptIndex,
    charIndex,
    handleViewportEnter,
    handleViewportLeave,
    showCursor: phase === "typing" || phase === "deleting" || phase === "pause",
  }
}
